// Dictate-or-type pad that lives in the bottom-right of the New Model
// form. Houses an editable textarea (always visible) so the operator
// can either dictate via the mic, type the description by hand, or
// dictate and then correct transcription errors before extracting.
//
// Modeled after the bug-report flow: textarea is the source of truth,
// the mic appends to it (only on stop, see below), and an explicit
// "Extract recipe" button fires the modal. Living without auto-extract
// is the trade-off for editability — the operator chooses when the
// transcript is good enough.
//
// Live merge note: rather than streaming Web Speech API chunks into
// the textarea (which would clobber manual edits and is awkward with
// interim results), we hold the captured transcript in a side bubble
// while recording, then concatenate it onto the textarea content when
// the session stops. From there the textarea is fully editable.
//
// Plan reference: docs/planning/mfg_recipe_verbal_authoring.md §3.2
// (entry point repositioned 2026-06-15 per follow-up feedback;
// textarea + explicit Extract added 2026-06-15 per second-pass
// feedback "make the dictate box typable and correctable").

import { useEffect, useState, type ReactNode } from "react";
import { useManufacturingDictateSession } from "./useManufacturingDictateSession";

const idleMicClass =
  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/40";
const recordingMicClass =
  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/40";
const extractBtnClass =
  "rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
const textareaClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 disabled:opacity-60";

function joinTranscriptChunks(existing: string, captured: string): string {
  const a = existing.trimEnd();
  const b = captured.trim();
  if (!b) return existing;
  if (!a) return b;
  return `${a} ${b}`;
}

export function ManufacturingModelsDictateMicButton({
  onComplete,
  disabled,
  extractLabel = "Extract recipe →",
  placeholder,
  clearOnExtract = false,
  help,
}: {
  /** Fires with the textarea contents when the operator clicks
   *  "Extract recipe". The panel reacts by opening the dictate
   *  review modal pre-seeded with the text. Return false to keep
   *  the textarea when clearOnExtract is set and processing failed. */
  onComplete: (transcript: string) => void | boolean | Promise<void | boolean>;
  disabled?: boolean;
  /** Primary action label (e.g. "Apply correction →" for BOM edit). */
  extractLabel?: string;
  placeholder?: string;
  /** Empty the textarea after onComplete succeeds. */
  clearOnExtract?: boolean;
  /** Sits on the button row, next to Dictate. */
  help?: ReactNode;
}) {
  const session = useManufacturingDictateSession();
  const recording = session.state === "recording";
  const [text, setText] = useState<string>("");

  useEffect(() => {
    if (session.state !== "stopped") return;
    const captured = session.transcript;
    session.reset();
    if (captured.trim().length > 0) {
      setText((prev) => joinTranscriptChunks(prev, captured));
    }
    // Only react to lifecycle transitions; reading the transcript and
    // reset setter inside the guarded branch is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.state]);

  const handleMicClick = () => {
    if (recording) {
      session.stop();
    } else {
      session.start();
    }
  };

  const handleExtract = () => {
    const value = text.trim();
    if (value.length === 0) return;
    void Promise.resolve(onComplete(value)).then((result) => {
      if (clearOnExtract && result !== false) setText("");
    });
  };

  const trimmed = text.trim();
  const canExtract = !disabled && !recording && trimmed.length > 0;
  const showMic = session.isSupported;

  return (
    <div className="w-full max-w-sm space-y-2">
      <textarea
        rows={4}
        className={textareaClass}
        placeholder={
          placeholder ??
          (showMic
            ? "Dictate with the mic or type the recipe here — code, name, each material with quantity…"
            : "Type the recipe description — code, name, each material with quantity…")
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={Boolean(disabled)}
      />

      {recording ? (
        <div className="rounded-md border border-red-200 bg-red-50/70 px-3 py-2 text-[11px] text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          <div className="mb-1 flex items-center gap-1 font-semibold">
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-red-600"
            />
            Listening — click Stop to add to the textarea
          </div>
          <div className="max-h-20 overflow-y-auto whitespace-pre-wrap text-gray-800 dark:text-zinc-100">
            {session.transcript || (
              <span className="text-gray-400 dark:text-zinc-500">(speak now…)</span>
            )}
          </div>
        </div>
      ) : null}

      {session.error ? (
        <div className="text-[10px] text-red-700 dark:text-red-300">
          {session.error}
        </div>
      ) : null}

      {!showMic ? (
        <div className="text-[10px] text-gray-500 dark:text-zinc-400">
          Voice dictation not supported in this browser — typing still works.
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        {help}
        {showMic ? (
          <button
            type="button"
            onClick={handleMicClick}
            disabled={Boolean(disabled)}
            aria-pressed={recording}
            aria-label={recording ? "Stop dictation" : "Start voice dictation"}
            title={recording ? "Stop dictation" : "Start voice dictation"}
            className={recording ? recordingMicClass : idleMicClass}
          >
            <span
              aria-hidden
              className={
                "inline-block h-1.5 w-1.5 rounded-full " +
                (recording ? "bg-red-600 animate-pulse" : "bg-violet-500")
              }
            />
            <span>{recording ? "Stop" : "Dictate"}</span>
          </button>
        ) : null}
        <button
          type="button"
          className={extractBtnClass}
          onClick={handleExtract}
          disabled={!canExtract}
          title={
            recording
              ? "Stop dictation first."
              : trimmed.length === 0
                ? "Type or dictate a description first."
                : undefined
          }
        >
          {extractLabel}
        </button>
      </div>
    </div>
  );
}
